import { useState } from "react";
import { motion } from "framer-motion";
import { Gamepad2, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

const CHIPS = [5, 10, 25, 50];
const USERNAME_RE = /^[A-Za-z0-9_]{3,16}$/;

export const DonatePanel = () => {
  const [username, setUsername] = useState(() => localStorage.getItem("ulti_mc_username") || "");
  const [amount, setAmount] = useState("10");
  const [loading, setLoading] = useState(false);

  const donate = async () => {
    const name = username.trim();
    const value = parseFloat(amount);
    if (!USERNAME_RE.test(name)) {
      toast.error("Enter a valid Minecraft username", { description: "3-16 letters, numbers or _" });
      return;
    }
    if (!value || value < 2 || value > 500) {
      toast.error("Enter an amount between $2 and $500");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/donations/checkout", { username: name, amount: value });
      localStorage.setItem("ulti_mc_username", name);
      window.location.assign(data.checkout_url);
    } catch (e) {
      setLoading(false);
      toast.error("Could not start donation", { description: formatApiError(e) });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="max-w-lg mx-auto rounded-2xl border border-white/10 bg-[#0D0B12]/80 p-8"
      data-testid="donate-panel"
    >
      <div className="text-center">
        <Heart className="w-10 h-10 text-[#8B5CF6] mx-auto fill-[#8B5CF6]/20" strokeWidth={1.5} />
        <h2 className="mt-4 font-display text-3xl tracking-tight text-white">Fuel the realm</h2>
        <p className="mt-2 text-sm text-slate-400 leading-relaxed">
          Donations go straight to hosting, development and events. Every dollar fills the community goal bar — and the Hall of Legends remembers you.
        </p>
      </div>

      <div className="mt-8 space-y-4">
        <div className="relative">
          <Gamepad2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Your Minecraft username"
            data-testid="donate-username-input"
            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]"
          />
        </div>

        <div className="flex gap-2">
          {CHIPS.map((chip) => (
            <button
              key={chip}
              onClick={() => setAmount(String(chip))}
              data-testid={`donate-chip-${chip}`}
              className={`flex-1 py-2.5 rounded-xl text-sm font-bold border transition-[background-color,border-color,color] duration-300 ${
                parseFloat(amount) === chip
                  ? "bg-[#8B5CF6] border-[#8B5CF6] text-white shadow-[0_0_20px_rgba(139,92,246,0.35)]"
                  : "bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-[#8B5CF6]/50"
              }`}
            >
              ${chip}
            </button>
          ))}
        </div>

        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
          <Input
            type="number"
            min="2"
            max="500"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Custom amount (min $2)"
            data-testid="donate-amount-input"
            className="pl-8 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]"
          />
        </div>

        <button
          onClick={donate}
          disabled={loading}
          data-testid="donate-submit-button"
          className="w-full py-4 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-60 text-white text-sm font-bold tracking-[0.15em] shadow-[0_0_30px_rgba(139,92,246,0.35)] transition-[background-color,opacity] duration-300 flex items-center justify-center gap-2"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> OPENING STRIPE…</>
          ) : (
            <>DONATE{parseFloat(amount) > 0 ? ` $${parseFloat(amount).toFixed(2)}` : ""}</>
          )}
        </button>
        <p className="text-[11px] text-center text-slate-600">Secure payment via Stripe. Currently in test mode.</p>
      </div>
    </motion.div>
  );
};
